import { IFeatureFlags } from './FeatureFlagsInterface';
import { EIPUplines, SVGUplines } from "../../utils";
import * as PlanIds from '../../planIds';

export default class FeatureFlagsBuilder {
    featureFlags: IFeatureFlags;

    constructor(
        featureFlagsFromServer: { [key: string]: any},
        featureFlagMetrics: { [key: string]: any}
    ) {
        this.featureFlags = this.buildFeatureFlags(featureFlagsFromServer, featureFlagMetrics);
    }

    private buildFeatureFlags(
        featureFlagsFromServer: { [key: string]: any },
        featureFlagMetrics: { [key: string]: any }): IFeatureFlags {
        return new FeatureFlags(
            featureFlagsFromServer['planExclusions'],
            featureFlagsFromServer['effectiveDateXMonthsOut'],
            featureFlagMetrics['createdAt'],
            featureFlagMetrics['parentId']
        )
    }
}
/* Notes on FeatureFlags
 * agent-level:
 *    feature flags can be defined here, ensuring we have all necessary data to determine if enabled or not for the agent metrics
 *    agent metrics available: created_at, ToH ID
 * member-level:
 *    member metrics available: state
 * environment-level
 *    feature flags currently are checked using environment.ts featureEnabled(ENV_VAR: string)
 *
 * When this class is initialized, it can be passed already-configured featureFlag values from the server
 * Or, if undefined, we can configure here using the necessary object's metrics (agent, member)
 */
class FeatureFlags implements IFeatureFlags {
    planExclusions: { [key: string]: boolean };
    effectiveDateXMonthsOut: number;

    // initialize with FeatureFlags from server, or as a bridge, the metrics that can be used to determine functionality
    constructor(
        planExclusions?: { [key: string]: boolean },
        effectiveDateXMonthsOut?: number,
        createdAt: Date = new Date(), // Default to current date if not provided
        parentId?: number,
    ) {
        // order-dependent merge of planExclusion objects: agent.createdAt takes precedence for plan exclusion rules
        this.planExclusions = planExclusions ?? this.getPlanExclusions(createdAt)
        this.effectiveDateXMonthsOut = effectiveDateXMonthsOut ?? this.getEffectiveDateXMonthsOut(parentId)
    }

    private getPlanExclusions(createdAt: Date): { [key: string]: boolean } {
        const exclusionsFromToday = this.getPlanExclusionsFromTodaysDate();
        const exclusionsFromAgentCreatedAt = this.getPlanExclusionsFromCreatedDate(createdAt);

        // Combine both exclusion sources
        // Get all unique plan IDs
        const allPlanIds = Array.from(new Set([
            ...Object.keys(exclusionsFromToday),
            ...Object.keys(exclusionsFromAgentCreatedAt)
        ]));

        // Create a combined result object
        // A plan is only visible if both sources allow it (true && true)
        return allPlanIds.reduce((result, planId) => {
            // For each plan, if ANY source says false, the result is false
            result[planId] =
                (exclusionsFromToday[planId] !== false) &&
                (exclusionsFromAgentCreatedAt[planId] !== false);
            return result;
        }, {} as { [key: string]: boolean });
    }
    // based on Agent's createdAt date
    private getPlanExclusionsFromCreatedDate(date: Date): { [key: string]: boolean } {
        const noSmilePlansAfterDate = new Date('2025-02-14');

        const showSmilePlans = date < noSmilePlansAfterDate;

        return {
            [PlanIds.NCDSmile1000byMetLife] : showSmilePlans,
            [PlanIds.NCDSmile2500byMetLife] : showSmilePlans,
        };
    }

    // plan exclusions based on today's date. Takes precedence over agent createdAt planExclusions
    private getPlanExclusionsFromTodaysDate(): { [key: string]: boolean } {
        const today = new Date();
        const showBrightPlansAfterDate = new Date('2025-04-01'); // TODO testing - when ready, change to 04/01 Start selling bright 4/1
        const smileCutoffDate = new Date('2025-05-01');

        const showBrightPlans = today >= showBrightPlansAfterDate;
        const showSmilePlans = today < smileCutoffDate;

        return {
            [PlanIds.NCDSmile1000byMetLife] : showSmilePlans,
            [PlanIds.NCDSmile2500byMetLife] : showSmilePlans,
            [PlanIds.NCDBright1000byMetLife] : showBrightPlans,
            [PlanIds.NCDBright1500byMetLife] : showBrightPlans
        };
    }

    private getPlanExclusionsForToH(parentId: number) {
        return 'write this'
    }

    // call this method to get the effectiveDateXMonthsOut value
    // when disabled, the default effectiveDate 2 months out from current billing month, is used
    private getEffectiveDateXMonthsOut(parentId?: number) {
        const defaultEffectiveDateXMonthsOut = 2
        if(!parentId) { return defaultEffectiveDateXMonthsOut };

        // uplines (parentIds) added here can select up to 6 months out from current date
        const effectiveDate6MonthsOut = [
        ...SVGUplines,
        // ...EIPUplines // Blocked: see https://app.clickup.com/t/9010034935/ENG2-3215
        ]

        return effectiveDate6MonthsOut.includes(parentId) ? 6 : defaultEffectiveDateXMonthsOut
    }

}
